import useSandboxes, { SandboxIsoStateIdsValues } from 'SandboxModule/hooks/useSandboxes';
import { useEffect, useCallback } from 'react';
import { useDynamicModal } from 'components/DynamicModalProvider/useDynamicModal';
import SimpleMenu from 'components/SimpleMenu';
import { ItemFilter, filterItemWithKeywords } from 'FiltersModule/utils';
import Box from '@mui/material/Box';
import { checkIsSandboxAccessible } from 'utils/sandbox';
import orderBy from 'lodash/orderBy';
import { RealmTypes } from 'RealmModule/constants/realm';
import { Realm, SimpleMenuItem } from 'types';
import { getRealmLabel } from 'RealmModule/utils/realm';
import { makeRealmLabel } from 'SandboxModule/utils';
import { useStateEffect } from 'hooks/useStateEffect';
import { Tenant } from 'TenantModule/types';
import { useRequestStatus } from 'RequestStatusModule/hooks/useRequestStatus';

type SandboxMenuItem = SimpleMenuItem & {
  value: string;
  name: Nullable<string>;
  isHeader?: boolean;
};

type SandboxHeaderMenuItem = SandboxMenuItem & { isHeader: true };

const makeHeaderMenuItem = (label: string): SandboxHeaderMenuItem => ({
  isHeader: true,
  label,
  disableButton: true,
  shouldAlwaysShow: true,
  className: 'group-header',
  value: 'sb-header',
  name: 'sb-header',
});

const itemFilter: ItemFilter = ({ searchWords, item }) =>
  filterItemWithKeywords({
    searchWords,
    item,
    filterKeysArray: ['value', 'name'],
  });

export const useSandboxDropdown = ({
  isoStateId,
  realmId,
}: {
  isoStateId: SandboxIsoStateIdsValues;
  /**
   * Initial selected realm id.
   * If not provided, the default realm will be selected.
   */
  realmId?: Realm['realm_id'];
}) => {
  const { openModal } = useDynamicModal();
  const {
    fetchSandboxes,
    getSandboxesByIsoState,
    getSandbox,
    getSelectedRailsSandbox,
    getDefaultRealm,
    handleSelectGlobalRailsSandbox,
  } = useSandboxes();

  const selectedGlobalRealm = getSelectedRailsSandbox() ?? null;

  // When the caller pins a realmId, never substitute a different realm — stay
  // unselected until the sandbox cache holds it (fetchSandboxes is in flight
  // on first mount). Falling back to the global realm here briefly showed —
  // and made submittable — a realm the caller never asked for.
  const initialRealm = realmId ? (getSandbox(realmId) ?? null) : selectedGlobalRealm;

  const [selectedLocalSandbox, setSelectedLocalSandbox] =
    useStateEffect<Nullable<Realm>>(initialRealm);

  useEffect(() => {
    fetchSandboxes(isoStateId);
  }, [fetchSandboxes, isoStateId]);

  const { requestStatus } = useRequestStatus({
    requestStatusId: isoStateId,
  });

  const isFetchingSandboxes = requestStatus?.isRequesting;

  const createMenuItem = (sb: Realm): SandboxMenuItem => ({
    label: makeRealmLabel({
      realmId: sb.realm_id,
      teamName: sb.team_name,
    }),
    value: sb.realm_id,
    name: sb.team_name?.toLocaleLowerCase() || '',
  });

  // THIS IS NOW TIED TO TENANT
  const openRealmDropdown = useCallback(
    ({
      target,
      isSelectingGlobalRealm = false,
      onSelect,
      primaryRealmId,
      realms,
    }: {
      target: HTMLElement;
      isSelectingGlobalRealm?: boolean;
      onSelect?: (args: { realm: Realm }) => void;
      realms: Tenant['realms'];
      primaryRealmId: Nullable<Realm['realm_id']>;
    }) => {
      const items: SandboxMenuItem[] = [];
      const sandboxes: SandboxMenuItem[] = [];
      const subscriptions: SandboxMenuItem[] = [];

      const primaryRealm = primaryRealmId ? getSandbox(primaryRealmId) : undefined;

      if (primaryRealm) {
        const primaryRealmMenuItem: SandboxMenuItem = {
          label: (
            <span>
              {primaryRealm.team_name} ({primaryRealm.realm_id}){' '}
              <Box fontSize={'12px'} color={'red'} display={'inline-block'}>
                | DO NOT SHARE
              </Box>
            </span>
          ),
          value: primaryRealm.realm_id,
          name: primaryRealm.team_name,
        };

        items.push(primaryRealmMenuItem);
      }

      realms.forEach((realmId) => {
        const sb = getSandbox(realmId);
        if (sb && checkIsSandboxAccessible(sb.status)) {
          if (sb.realm_type === RealmTypes.sandbox && sb.realm_id !== primaryRealmId) {
            sandboxes.push(createMenuItem(sb));
          }
          if (sb.realm_type === RealmTypes.subscription && sb.realm_id !== primaryRealmId) {
            subscriptions.push(createMenuItem(sb));
          }
        }
      });

      if (sandboxes.length > 0) {
        items.push(makeHeaderMenuItem(getRealmLabel(RealmTypes.sandbox)));
        items.push(...orderBy(sandboxes, ['value'], ['asc']));
      }

      if (subscriptions.length > 0) {
        items.push(makeHeaderMenuItem(getRealmLabel(RealmTypes.subscription)));
        items.push(...orderBy(subscriptions, ['value'], ['asc']));
      }

      const onClick = ({ item }: { item: SandboxMenuItem }) => {
        const realm = getSandbox(item.value);
        if (realm) {
          if (isSelectingGlobalRealm) {
            handleSelectGlobalRailsSandbox({ sandbox: realm });
            return;
          }

          // Local select
          onSelect?.({ realm });
          setSelectedLocalSandbox(realm);
        }
      };

      openModal({
        modal: ({ isOpen: _isOpen, toggle }) => {
          return (
            <SimpleMenu<SandboxMenuItem>
              anchorEl={target}
              items={items}
              handleClickV2={onClick}
              handleClose={toggle}
              showSearchBar
              itemFilter={itemFilter}
              transformMenu="translateX(0px) translateY(0px)"
              matchAnchorWidth
              noMarginTop
              menuMaxHeight={316}
            />
          );
        },
      });
    },
    [getSandbox, openModal, setSelectedLocalSandbox, handleSelectGlobalRailsSandbox],
  );

  return {
    openRealmDropdown,
    selectedGlobalRealm,
    selectedLocalSandbox,
    handleSelectGlobalRailsSandbox,
    isFetchingSandboxes,
  };
};
